import type { RichMark, RichMarkType, RichNode, Uuid } from '@cyphex/types';
import { mentionAt, type MentionTarget } from './mentions';

/* Group order: 1 **b** · 2 __b__ · 3 *i* · 4 _i_ · 5 ~~s~~ · 6 `c` ·
   7/8 [label](href) · 9 bare url · 10 the @ that may open a mention
   (never one glued to an address: "sai@wmlit.com" is an email, not a chip)
   Underscores only mark text at a word boundary, as in CommonMark: snake_case
   and my_var_2 stay literal instead of losing their underscores to italics. */
const INLINE_RE =
  /\*\*([\s\S]+?)\*\*|(?<![A-Za-z0-9])__([\s\S]+?)__(?![A-Za-z0-9])|\*([^*\n]+?)\*|(?<![A-Za-z0-9])_([^_\n]+?)_(?![A-Za-z0-9])|~~([\s\S]+?)~~|`([^`\n]+)`|\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+[^\s<.,:;"')\]])|(?<![A-Za-z0-9._%+-])(@)(?=\S)/g;

const MAX_DEPTH = 4;

function withMark(nodes: RichNode[], mark: RichMark): RichNode[] {
  return nodes.map((node) =>
    node.type === 'text' ? { ...node, marks: [...(node.marks ?? []), mark] } : node,
  );
}

function text(value: string, mark?: RichMarkType): RichNode {
  return mark ? { type: 'text', text: value, marks: [{ type: mark }] } : { type: 'text', text: value };
}

/**
 * Parses one paragraph of markdown into ProseMirror-compatible inline nodes.
 *
 * `targets` is who an `@Name` may resolve to - the people in the conversation.
 * An `@` that names none of them stays TEXT: a chip is a promise that somebody
 * was told, and the id-less chips this used to emit notified nobody.
 */
export function parseInline(
  source: string,
  depth = 0,
  targets: readonly MentionTarget[] = [],
): RichNode[] {
  if (!source) return [];
  if (depth >= MAX_DEPTH) return [text(source)];

  const out: RichNode[] = [];
  let cursor = 0;
  /* A FRESH regex per call. This function recurses into **bold** and _italic_,
     and a shared /g regex let the inner call run `lastIndex` back to 0 - the
     outer loop then found the same **bold** again, forever, and the tab ran out
     of memory on the first message with a mark in it. */
  const re = new RegExp(INLINE_RE.source, 'g');

  for (let m = re.exec(source); m !== null; m = re.exec(source)) {
    const mention = m[10] === undefined ? null : mentionAt(source, m.index, targets);
    // Nobody by that name here: the `@` is an ordinary character of the text.
    if (m[10] !== undefined && mention === null) continue;

    if (m.index > cursor) out.push(text(source.slice(cursor, m.index)));
    cursor = m.index + m[0].length;

    const strong = m[1] ?? m[2];
    const emphasis = m[3] ?? m[4];

    if (strong !== undefined) out.push(...withMark(parseInline(strong, depth + 1, targets), { type: 'bold' }));
    else if (emphasis !== undefined) out.push(...withMark(parseInline(emphasis, depth + 1, targets), { type: 'italic' }));
    else if (m[5] !== undefined) out.push(...withMark(parseInline(m[5], depth + 1, targets), { type: 'strike' }));
    else if (m[6] !== undefined) out.push(text(m[6], 'code'));
    else if (m[7] !== undefined && m[8] !== undefined) {
      out.push({ type: 'text', text: m[7], marks: [{ type: 'link', attrs: { href: m[8] } }] });
    } else if (m[9] !== undefined) {
      out.push({ type: 'text', text: m[9], marks: [{ type: 'link', attrs: { href: m[9] } }] });
    } else if (mention !== null) {
      out.push({
        type: 'mention',
        attrs: {
          mentionKind: mention.target.kind,
          ...(mention.target.id === null ? {} : { mentionId: mention.target.id as Uuid }),
          label: mention.target.label,
        },
      });
      // The name may hold spaces, so the mention runs past what the regex took.
      cursor = m.index + mention.length;
      re.lastIndex = cursor;
    }
  }

  if (cursor < source.length) out.push(text(source.slice(cursor)));
  return out;
}

/** Every `https://…` in a plain string — drives client-side link unfurling. */
export function extractUrls(source: string): string[] {
  const matches = source.match(/https?:\/\/[^\s<]+[^\s<.,:;"')\]]/g);
  return matches ? Array.from(new Set(matches)) : [];
}
